"use client";
import { useEffect, useState, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { docStore, globalStore } from '@/app/stores';
import style from "@/assets/styling/scss/navbar.module.scss";
import { parseDocs, gatherFilesContentFromParsedDocs, translate } from '@/assets/utils/utils';
import { observer } from 'mobx-react';

interface FileData {
  name: string;
  content: string;
  path: string;
}

const SearchWrapper = observer(() => {
  const { lang } = globalStore;

  const [query, setQuery] = useState("");
  const [allFiles, setAllFiles] = useState<FileData[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<FileData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const placeholder = useMemo(() => translate(lang, 'search_placeholder'), [lang]);
  const router = useRouter();

  const spanRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState(150);

  useEffect(() => {
    if (spanRef.current) {
      const spanWidth = spanRef.current.getBoundingClientRect().width;
      setInputWidth(spanWidth + 45);
    }
  }, [placeholder]);

  useEffect(() => {
    async function fetchDocs() {
      try {
        const response = await fetch(`/data/doctree.json`);
        if (!response.ok) throw new Error("Network response was not ok");

        const data = await response.json();
        const parsedDocs = parseDocs(data);
        const filesData = gatherFilesContentFromParsedDocs(parsedDocs);
        setAllFiles(filesData);
      } catch (error) {
        console.error("Failed to fetch documentation:", error);
      }
    }

    fetchDocs();
  }, []);

  useEffect(() => {
    if (query) {
      const suggestions = allFiles.filter(file =>
        file.content.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredSuggestions(suggestions);
    } else {
      setFilteredSuggestions([]);
      setSelectedIndex(-1);
    }
  }, [query, allFiles]);

  const handleSuggestionClick = (file: FileData) => {
    docStore.setSelectedFile(file.content, file.path);
    router.push('/for-developers/documentation/');
    setQuery('');
    setFilteredSuggestions([]);
    setSelectedIndex(-1);
  };

  const handleSearch = () => {
    if (filteredSuggestions.length > 0) {
      handleSuggestionClick(filteredSuggestions[selectedIndex >= 0 ? selectedIndex : 0]);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && filteredSuggestions.length > 0) {
      handleSuggestionClick(filteredSuggestions[selectedIndex]);
    } else if (event.key === "ArrowDown") {
      setSelectedIndex(prevIndex =>
        (prevIndex < filteredSuggestions.length - 1) ? prevIndex + 1 : 0
      );
    } else if (event.key === "ArrowUp") {
      setSelectedIndex(prevIndex =>
        (prevIndex > 0) ? prevIndex - 1 : filteredSuggestions.length - 1
      );
    }
  };

  return (
    <div className={style.searchWrapper} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder={placeholder}
        className={style.search}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{ width: `${inputWidth}px` }}
      />
      <span
        ref={spanRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "pre",
          fontSize: "inherit",
          fontFamily: "inherit",
          fontWeight: "inherit",
        }}
      >
        {placeholder}
      </span>
      <MagnifyingGlassIcon className={style.searchIcon} onClick={handleSearch} />
      {filteredSuggestions.length > 0 && (
        <ul className={style.suggestionsList}>
          {filteredSuggestions.map((file, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionClick(file)}
              style={{
                backgroundColor: selectedIndex === index ? 'var(--background-color)' : 'transparent',
                color: selectedIndex === index ? 'var(--accent)' : '#000'
              }}
            >
              {file.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default SearchWrapper;

